import React from "react";
import { unassignedInvoicesPanelSection } from "./UnassignedInvoicesPanel";
import { customAppLabelTranslationsSection } from "./CustomAppLabelTranslations";
import { DetailSectionTemplate } from "components/MainSectionTemplate/DetailSectionTemplate";
import { clientMainSection } from "./Sections/clientMainSection";
import { discountSection } from "./Sections/discountSection";
import { invoicingSection } from "./Sections/invoicingSection";
import { billingDetailsSection } from "./Sections/billingDetailsSection";
import { paymentMethodSection } from "./Sections/paymentMethodSection";
import { vatRatesPanelSection } from "./VatRatesPanel";
import { subscriptionPanelSection } from "./SubscriptionsPanel";
import { functionGroupsPanelSection } from "./FunctionGroupsPanel";
import { hubSpotDealPanelSection } from "../../HubSpot/HubSpotDealPanel";
import moment from "moment";
import { bookkeepingConfigurationSection } from "./BookkeepingConfiguration";

type Props = server.viewmodel.ClientDetailFrontModel;

const ClientDetailsApp = (props: Props) => {
  const [payPerSepaAllowed, setPayPerSepaAllowed] = React.useState(props.billingDetails.payPerSepaAllowed);
  const [payPerInvoiceAllowed, setPayPerInvoiceAllowed] = React.useState(props.billingDetails.payPerInvoiceAllowed);
  const [selectedInvoiceWordTemplateId, setSelectedInvoiceWordTemplateId] = React.useState(props.client.invoiceWordTemplateId);
  const [selectedInvoiceEmailTemplateId, setSelectedInvoiceEmailTemplateId] = React.useState(props.client.invoiceEmailTemplateId);
  const [billingCompany, setBillingCompany] = React.useState(props.billingDetails.company);
  const [billingContact, setBillingContact] = React.useState(props.billingDetails.contact);
  const [billingContactEmail, setBillingContactEmail] = React.useState(props.billingDetails.contactEmail);
  const [paymentMethod, setPaymentMethod] = React.useState(props.billingDetails.paymentMethodType);
  const [customerSince, setCustomerSince] = React.useState(props.client.customerSince ? moment.utc(props.client.customerSince) : null);
  const [customerChurn, setCustomerChurn] = React.useState(props.client.customerChurn ? moment.utc(props.client.customerChurn) : null);
  const [discountPercentage, setDiscountPercentage] = React.useState(props.discount?.percentage.value ?? 0)
  const [discountUntil, setDiscountUntil] = React.useState(props.discount?.until ? moment.utc(props.discount.until) : null);
  const [discountNote, setDiscountNote] = React.useState(props.discount?.note ?? "");

  const resetBillingData = async () => {
    const billingRes = await window.CoreApi.Client.GetClientBillingDetails(props.client.clientId);
    setBillingContact(billingRes.data.data.contact);
    setBillingContactEmail(billingRes.data.data.contactEmail);
    setBillingCompany(billingRes.data.data.company);
    setPayPerInvoiceAllowed(billingRes.data.data.payPerInvoiceAllowed);
    setPayPerSepaAllowed(billingRes.data.data.payPerSepaAllowed);
    setPaymentMethod(billingRes.data.data.paymentMethodType);
  }

  const onValueChanged = async (key: string, value: any) => {
    if (key === "payPerSepa") {
      setPayPerSepaAllowed(value);
    } else if (key === "payPerInvoice") {
      setPayPerInvoiceAllowed(value);
    } else if (key === "wordTemplate") {
      setSelectedInvoiceWordTemplateId(value.key);
    } else if (key === "emailTemplate") {
      setSelectedInvoiceEmailTemplateId(value.key);
    } else if (key === "billingCompany") {
      setBillingCompany(value);
    } else if (key === "billingContact") {
      setBillingContact(value);
    } else if (key === "customerSince") {
      setCustomerSince(value ? moment.utc(value) : null);
    } else if (key === "customerChurn") {
      setCustomerChurn(value ? moment.utc(value) : null);
    } else if (key === "discountPercentage") {
      setDiscountPercentage(value);
    } else if (key === "discountUntil") {
      setDiscountUntil(value ? moment.utc(value) : null);
    } else if (key === "discountNote") {
      setDiscountNote(value);
    } else if (key === "discountYesOrNo") {
      if (value) {
        setDiscountPercentage(0.1);
      } else {
        setDiscountPercentage(0);
        setDiscountNote("");
        setDiscountUntil(null);
      }
    }
  }

  const keysToResetBillingData = ["payPerSepa", "payPerInvoice", "billingCompany", "billingContact"];
  const onSuccessfulUpdate = async (key: string) => {
    if (keysToResetBillingData.includes(key)) {
      await resetBillingData();
    }
  }

  return <DetailSectionTemplate
    onValueChanged={onValueChanged}
    onSuccessfulUpdate={onSuccessfulUpdate}
    isInPageEditable
    sections={[
      clientMainSection({
        client: props.client,
        customerSince,
        customerChurn,
        activeSubscription: props.activeSubscription,
        nextSubscription: props.nextSubscription,
        discount: props.discount,
        billingDetails: props.billingDetails,
        subscriptions: props.subscriptions
      }),
      hubSpotDealPanelSection(props.client.hubSpotDealId, props.client.clientId),
      discountSection({
        clientId: props.client.clientId,
        discountNote,
        discountPercentage,
        discountUntil
      }),
      invoicingSection({
        invoiceWordTemplates: props.invoiceWordTemplates,
        invoiceEmailTemplates: props.invoiceEmailTemplates,
        selectedInvoiceWordTemplateId: selectedInvoiceWordTemplateId,
        selectedInvoiceEmailTemplateId: selectedInvoiceEmailTemplateId,
        clientId: props.client.clientId
      }),
      billingDetailsSection({
        contact: billingContact,
        contactEmail: billingContactEmail,
        company: billingCompany,
        clientId: props.client.clientId
      }),
      paymentMethodSection({
        clientId: props.client.clientId,
        payPerSepaAllowed,
        payPerInvoiceAllowed,
        paymentMethod
      }),
      functionGroupsPanelSection(props.functionGroups),
      vatRatesPanelSection(props.vatRates, props.client),
      subscriptionPanelSection(props),
      unassignedInvoicesPanelSection(props.unassignedInvoices),
      customAppLabelTranslationsSection(props.client.clientId, props.customAppLabelTranslations),
      bookkeepingConfigurationSection(props.client.clientId),
    ]}
  />
};

export default ClientDetailsApp;
